"use client";

import type { CreateSoundboardRequest } from "@/app/_api/soundboards";

import { createSoundboard, deleteSoundboard, updateSoundboard } from "@/app/_api/soundboards";
import { logger } from "@utilities/logger";
import { toast } from "@/app/_providers/toast";
import { TrashIcon } from "lucide-react";
import { useTranslations } from "next-intl";

import type { Preset, Sound } from "../types";

import { DEFAULT_ICONS, ICON_IDS } from "../data";
import {
  createSoundboardSuccess,
  deleteLocalPreset,
  deletePresetSuccess,
  updateSoundboardSuccess,
} from "../state/actions";
import { useSoundboard } from "../state/reducer";
import { ShareButton } from "./ShareButton";
import { saveStateToSessionCookie } from "./utils";

export const SaveFooter = ({ preset }: { preset?: Preset }) => {
  const { state, dispatch } = useSoundboard();
  const t = useTranslations("ClientKeys.soundboard.preset_editor");

  if (!preset) {
    return null;
  }

  const savePreset = async () => {
    if (!state.user) {
      logger.error(null, "No user found");
      return;
    }

    const token = await state.user.getIdToken();

    const requestData: CreateSoundboardRequest = {
      name: preset.name,
      icon_id: DEFAULT_ICONS[ICON_IDS.CUSTOM],
      sounds: preset.sounds
        .filter((sound): sound is Sound => sound.type !== "placeholder")
        .map(sound => ({
          name: sound.name,
          type: sound.url.startsWith("http") ? "default" : "custom",
          history_item_id: sound.historyItemId,
          default_sound_id: sound.historyItemId ? undefined : sound.defaultSoundId,
          sound_id: sound.savedSoundId,
        })),
    };

    if (preset.isCreatedByMe && preset.savedBoardId) {
      const response = await updateSoundboard(preset.id, requestData, token);
      dispatch(updateSoundboardSuccess({ soundboard: response }));
      toast.success(t("preset_updated"));
    } else if (!preset.savedBoardId) {
      const response = await createSoundboard(requestData, token);
      dispatch(createSoundboardSuccess({ soundboard: response, existingPresetId: preset.id }));
      toast.success(t("preset_created"));
    }
  };

  const deletePreset = async () => {
    if (!preset.savedBoardId) {
      dispatch(deleteLocalPreset({ presetId: preset.id }));
      toast.success(t("preset_deleted"));
    } else {
      const token = await state.user?.getIdToken();
      if (!token) return;
      await deleteSoundboard(preset.id, token);
      dispatch(deletePresetSuccess({ presetId: preset.id }));
      toast.success(t("preset_deleted"));
    }
  };

  return (
    <div className='flex gap-4'>
      {state.user ? (
        <div className='border-top flex w-full gap-4 border-default p-16'>
          {preset.isCreatedByMe && (
            <button
              className='sb-bg-red sb-color-red mr-16 flex items-center justify-center rounded-sm p-12'
              onClick={() => deletePreset()}
            >
              <TrashIcon size={16} />
            </button>
          )}
          <ShareButton />
          <button
            className='font-soundboard f-ui-04 w-full flex-1 rounded-sm border bg-inverse px-16 py-12 text-inverse disabled:opacity-50'
            disabled={!preset.hasEdits}
            onClick={() => savePreset()}
          >
            {preset.savedBoardId ? t("update_preset") : t("save_preset")}
          </button>
        </div>
      ) : (
        <div className='flex w-full flex-col'>
          <div className='bg-dark p-16'>
            <p className='font-soundboard f-ui-05 font-semibold normal-case text-light'>
              {t("create_account_to_save_preset")}
            </p>
          </div>
          <div className='border-top flex border-default p-16'>
            <a
              className='font-soundboard f-ui-04 w-full rounded-sm bg-inverse px-16 py-12 text-center text-inverse'
              href={`/app/sign-up?redirect=${encodeURIComponent(`/sound-effects/soundboard`)}`}
              onClick={() => saveStateToSessionCookie(state)}
              rel='noreferrer'
              target='_blank'
            >
              {t("create_account")}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
